"use client"
import React from 'react';
import { motion } from 'motion/react';
import { BarChart3, MessageSquare, Database, Shield, ArrowRight } from 'lucide-react';
import { TCompaniesConfig } from '@/app/config';

const BusinessFunctions= ({config}:{config:TCompaniesConfig["BusinessFunctions"]}) => {
  const businessFunctionsIcons = [
    {
      icon: BarChart3,
   
    },
    {
      icon: MessageSquare,
     
    },
    {
      icon: Database,
      
    },
    {
      icon: Shield,
      
    }
  ];
  const businessFunctions = config.businessFunctions 

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

 


  const cardHoverVariants = {
    hover: {
      y: -8,
      scale: 1.02,
      rotateY: 2,
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const backgroundVariants = {
    hover: {
      background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
      transition: {
        duration: 0.3
      }
    }
  };


  return (
    <motion.section 
      className="py-20 bg-gray-50 relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
        >
          <motion.h2 
            className="text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
              {config.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Business Functions Grid */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          variants={containerVariants}
        >
          {businessFunctions.map((func, index) => {
            const IconComponent = businessFunctionsIcons[index].icon;
            return (
              <motion.div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-sm cursor-pointer relative overflow-hidden"
                variants={ {
                  hidden: { 
                    opacity: 0, 
                    y: 60,
                    scale: 0.9,
                    rotateY: -15
                  },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    rotateY: 0,
                    transition: {
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }
                }}
                whileHover="hover"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                {...cardHoverVariants}
                style={{ perspective: "1000px" }}
              >
                {/* Animated background overlay */}
                <motion.div
                  className="absolute inset-0 opacity-0"
                  variants={backgroundVariants}
                  whileHover="hover"
                  style={{ zIndex: -1 }}
                />

                {/* Header with icon and title */}
                <motion.div 
                  className="flex items-center space-x-3 mb-6"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className={`w-12 h-12 ${func.iconBg} rounded-xl flex items-center justify-center relative`}
                    variants={{
                      hidden: { 
                        scale: 0,
                        rotate: -90,
                        opacity: 0
                      },
                      visible: {
                        scale: 1,
                        rotate: 0,
                        opacity: 1,
                        transition: {
                          type: "spring",
                          stiffness: 200,
                          damping: 15,
                          delay: 0.2
                        }
                      },
                      hover: {
                        scale: 1.15,
                        rotate: [0, -10, 10, 0],
                        backgroundColor: "#16a34a",
                        transition: {
                          duration: 0.4,
                          ease: "easeInOut"
                        }
                      }
                    }}
                    whileHover="hover"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      variants={{
                        hidden: { scale: 0 },
                        visible: {
                          scale: 1,
                          transition: {
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            delay: 0.3
                          }
                        },
                        hover: {
                          scale: 1.2,
                          color: "#ffffff",
                          transition: {
                            duration: 0.2,
                            ease: "easeInOut"
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.1 }}
                    >
                      <IconComponent className={`w-6 h-6 ${func.iconColor}`} />
                    </motion.div>

                    {/* Pulse effect around icon */}
                    <motion.div
                      className="absolute inset-0 bg-green-200 rounded-xl opacity-0"
                      whileHover={{
                        scale: [1, 1.3, 1],
                        opacity: [0, 0.3, 0],
                      }}
                      transition={{ duration: 0.6 }}
                    />
                  </motion.div>
                  
                  <motion.h3 
                    className="text-2xl font-bold text-gray-900"
                    variants={{
                      hidden: { opacity: 0, x: -30 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                          delay: 0.4
                        }
                      },
                      hover: {
                        color: "#16a34a",
                        scale: 1.05,
                        transition: {
                          duration: 0.2
                        }
                      }
                    }}
                    whileHover="hover"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                  >
                    {func.title}
                  </motion.h3>
                </motion.div>

                {/* Features list */}
                <motion.div 
                  className="space-y-4"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  {func.features.map((feature, featureIndex) => (
                    <motion.div 
                      key={featureIndex} 
                      className="flex items-start space-x-3"
                      variants={{
                        hidden: { opacity: 0, x: -40 },
                        visible: {
                          opacity: 1,
                          x: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut"
                          }
                        },
                        hover: {
                          x: 8,
                          transition: {
                            duration: 0.2,
                            ease: "easeOut"
                          }
                        }
                      }}
                      whileHover="hover"
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + featureIndex * 0.1 + 0.5 }}
                    >
                      <motion.div
                        variants={{
                          hidden: { scale: 0, rotate: -45 },
                          visible: {
                            scale: 1,
                            rotate: 0,
                            transition: {
                              type: "spring",
                              stiffness: 400,
                              damping: 25
                            }
                          },
                          hover: {
                            x: 6,
                            scale: 1.3,
                            rotate: 90,
                            color: "#16a34a",
                            transition: {
                              duration: 0.2,
                              ease: "easeOut"
                            }
                          }
                        }}
                        whileHover="hover"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + featureIndex * 0.1 + 0.6 }}
                      >
                        <ArrowRight className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      </motion.div>
                      <motion.span 
                        className="text-gray-600 leading-relaxed"
                        whileHover={{ 
                          color: "#16a34a",
                          fontWeight: 500
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {feature}
                      </motion.span>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Shimmer effect on hover */}
                <motion.div
                  className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0"
                  initial={{ x: "-100%" }}
                  whileHover={{
                    x: "100%",
                    opacity: 0.1,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut"
                  }}
                  style={{
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)"
                  }}
                />

                {/* Corner accent */}
                <motion.div
                  className="absolute top-4 right-4 w-2 h-2 bg-green-400 rounded-full opacity-0"
                  whileHover={{ opacity: 1, scale: [1, 1.5, 1] }}
                  transition={{ duration: 0.4 }}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Floating decorative elements */}
        <motion.div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-3 h-3 rounded-full ${
                i % 2 === 0 ? 'bg-green-200' : 'bg-green-300'
              }`}
              style={{
                left: `${15 + i * 20}%`,
                top: `${20 + (i * 15) % 60}%`,
              }}
              variants={ {
                animate: {
                  y: [-10, 10, -10],
                  rotate: [0, 5, -5, 0],
                  transition: {
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }
                }
              }}
              animate="animate"
              transition={{ delay: i * 0.5 }}
            />
          ))}
        </motion.div>

        {/* Central connecting element */}
        <motion.div
          className="absolute top-1/2 left-1/2 w-1 h-1 bg-green-400 rounded-full -translate-x-1/2 -translate-y-1/2"
          variants={{
            animate: {
              scale: [1, 1.05, 1],
              transition: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }
          }}
          animate="animate"
          style={{ zIndex: -1 }}
        />
      </div>
    </motion.section>
  );
};

export default BusinessFunctions;